// Merge fields for public pages: substitute {{tokens}} in page copy with data
// passed from a booking (or form) handoff. Pure + unit-tested. Used by PageView
// when a /p/ page is loaded with booking data (resolved from a confirm token
// and/or raw query params). Unknown tokens render empty so half-filled copy
// never shows raw {{braces}} to a visitor.
import type { Section } from './pages';

/** Read-only booking data resolved server-side from a confirm token. */
export interface ResolvedMergeData {
  name?: string; email?: string; when?: string; date?: string; time?: string;
  meet_url?: string; title?: string; duration?: string; note?: string;
  answers?: Record<string, string>;
}

/** Replace every {{token}} in `text` using `ctx` (keys are lowercase). Tokens are
 *  matched case-insensitively and may contain spaces ({{ first_name }}). Answer
 *  tokens look like {{answer:Budget}}. A literal fallback after the first pipe
 *  ({{first_name|there}}) is used when the value is missing or blank. Unknown
 *  tokens without a fallback become ''. Values are plain text, not HTML. */
export function applyMergeFields(text: string, ctx: Record<string, string>): string {
  if (!text || text.indexOf('{{') === -1) return text;
  return text.replace(/\{\{\s*([^{}]+?)\s*\}\}/g, (_m, raw: string) => {
    const split = raw.indexOf('|');
    const key = (split < 0 ? raw : raw.slice(0, split)).trim().toLowerCase();
    const fallback = split < 0 ? '' : raw.slice(split + 1).trim();
    const value = Object.prototype.hasOwnProperty.call(ctx, key) ? ctx[key] : undefined;
    return typeof value === 'string' && value.trim() ? value : fallback;
  });
}

/** Build the lowercase merge context from token-resolved booking data plus any
 *  raw URL params. Resolved data wins over params on conflict. Derives
 *  `first_name` from `name`, and flattens `answers` to `answer:<label>` keys. */
export function buildMergeContext(
  resolved?: ResolvedMergeData | null,
  params?: Record<string, string> | null,
): Record<string, string> {
  const ctx: Record<string, string> = {};
  if (params) for (const [k, v] of Object.entries(params)) if (v != null) ctx[k.toLowerCase()] = String(v);
  if (ctx.name?.trim() && !ctx.first_name?.trim()) ctx.first_name = ctx.name.trim().split(/\s+/)[0];
  if (resolved) {
    const { answers, ...rest } = resolved;
    for (const [k, v] of Object.entries(rest)) if (v != null && v !== '') ctx[k.toLowerCase()] = String(v);
    if (rest.name) ctx.first_name = String(rest.name).trim().split(/\s+/)[0];
    if (answers) for (const [label, val] of Object.entries(answers)) if (val != null) ctx[`answer:${label.toLowerCase()}`] = String(val);
  }
  return ctx;
}

/** Return a copy of `section` with every user-facing string field (and nested
 *  array text) run through applyMergeFields. Does not mutate the input. */
export function mergeSection(section: Section, ctx: Record<string, string>): Section {
  const m = (v: string) => applyMergeFields(v, ctx);
  // Preserve existing link-token support, but never let URL-provided values
  // introduce executable protocols. New picker controls only edit copy.
  const link = (v: string) => {
    if (!v.includes('{{')) return v;
    const next = m(v).trim();
    if (!next || /[\u0000-\u001f\u007f\\]/.test(next)) return '';
    try {
      return ['http:', 'https:', 'mailto:', 'tel:'].includes(new URL(next, 'https://page.invalid').protocol) ? next : '';
    } catch { return ''; }
  };
  const out: Section = { ...section };
  if (typeof out.eyebrow === 'string') out.eyebrow = m(out.eyebrow);
  if (typeof out.headline === 'string') out.headline = m(out.headline);
  if (typeof out.subheadline === 'string') out.subheadline = m(out.subheadline);
  if (typeof out.heading === 'string') out.heading = m(out.heading);
  if (typeof out.body === 'string') out.body = m(out.body);
  if (typeof out.quote === 'string') out.quote = m(out.quote);
  if (typeof out.attribution === 'string') out.attribution = m(out.attribution);
  if (typeof out.role === 'string') out.role = m(out.role);
  if (typeof out.label === 'string') out.label = m(out.label);
  if (typeof out.cta_label === 'string') out.cta_label = m(out.cta_label);
  if (typeof out.cta2_label === 'string') out.cta2_label = m(out.cta2_label);
  if (typeof out.href === 'string') out.href = link(out.href);
  if (typeof out.alt === 'string') out.alt = m(out.alt);
  if (typeof out.left_title === 'string') out.left_title = m(out.left_title);
  if (typeof out.right_title === 'string') out.right_title = m(out.right_title);
  if (out.items) out.items = out.items.map((x) => (typeof x === 'string' ? m(x) : x));
  if (out.left_items) out.left_items = out.left_items.map(m);
  if (out.right_items) out.right_items = out.right_items.map(m);
  if (out.features) out.features = out.features.map((f) => ({ ...f, title: m(f.title), body: typeof f.body === 'string' ? m(f.body) : f.body }));
  if (out.faqs) out.faqs = out.faqs.map((f) => ({ q: m(f.q), a: m(f.a) }));
  if (out.steps) out.steps = out.steps.map((st) => ({ label: m(st.label), detail: typeof st.detail === 'string' ? m(st.detail) : st.detail }));
  if (out.stats) out.stats = out.stats.map((st) => ({ value: m(st.value), label: m(st.label) }));
  if (out.links) out.links = out.links.map((l) => ({ label: m(l.label), href: link(l.href) }));
  if (out.plans) out.plans = out.plans.map((p) => ({
    ...p, name: m(p.name), price: m(p.price), features: p.features.map(m),
    period: typeof p.period === 'string' ? m(p.period) : p.period,
    description: typeof p.description === 'string' ? m(p.description) : p.description,
    price_compare: typeof p.price_compare === 'string' ? m(p.price_compare) : p.price_compare,
    badge: typeof p.badge === 'string' ? m(p.badge) : p.badge,
    cta_label: typeof p.cta_label === 'string' ? m(p.cta_label) : p.cta_label,
  }));
  if (out.members) out.members = out.members.map((p) => ({ ...p, name: m(p.name), role: typeof p.role === 'string' ? m(p.role) : p.role }));
  if (typeof out.expired_text === 'string') out.expired_text = m(out.expired_text);
  // v2.58 funnel fields (absent on older sections, so those are unchanged).
  if (typeof out.highlight === 'string') out.highlight = m(out.highlight);
  if (typeof out.proof_text === 'string') out.proof_text = m(out.proof_text);
  if (typeof out.cta_note === 'string') out.cta_note = m(out.cta_note);
  if (typeof out.price_note === 'string') out.price_note = m(out.price_note);
  if (typeof out.price === 'string') out.price = m(out.price);
  if (typeof out.price_compare === 'string') out.price_compare = m(out.price_compare);
  if (out.quotes) out.quotes = out.quotes.map((q) => ({ ...q, quote: m(q.quote), name: m(q.name), role: typeof q.role === 'string' ? m(q.role) : q.role }));
  if (out.modules) out.modules = out.modules.map((md) => ({ ...md, title: m(md.title), label: typeof md.label === 'string' ? m(md.label) : md.label, body: typeof md.body === 'string' ? m(md.body) : md.body, lessons: md.lessons?.map((l) => (typeof l === 'string' ? m(l) : l)) }));
  if (out.offer_items) out.offer_items = out.offer_items.map((o) => ({ ...o, label: m(o.label), value: typeof o.value === 'string' ? m(o.value) : o.value }));
  return out;
}
